import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Layers, Eye, EyeOff, AlertCircle, CheckCircle, Check } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const PASSWORD_RULES = [
  { test: (p) => p.length >= 8,      label: 'At least 8 characters' },
  { test: (p) => /[A-Z]/.test(p),    label: 'One uppercase letter' },
  { test: (p) => /[a-z]/.test(p),    label: 'One lowercase letter' },
  { test: (p) => /\d/.test(p),       label: 'One number' },
];

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  if (!token) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <h1 className="text-white font-bold text-xl mb-2">Invalid reset link</h1>
        <p className="text-gray-400 mb-4">This link is missing a token. Please request a new one.</p>
        <Link to="/forgot-password" className="text-green-400 hover:text-green-300">Request new link →</Link>
      </div>
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const failing = PASSWORD_RULES.filter(r => !r.test(password));
    if (failing.length > 0) { setError('Password does not meet requirements'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4 pt-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30">
              <Layers className="w-6 h-6 text-gray-950" />
            </div>
            <span className="text-white font-bold text-2xl">Plarix<span className="text-green-400">MC</span></span>
          </Link>
          <h1 className="text-2xl font-black text-white">Set new password</h1>
          <p className="text-gray-400 mt-1">Choose a strong password for your account</p>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
          {done ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/10 border-2 border-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-white font-bold text-lg mb-2">Password reset!</h2>
              <p className="text-gray-400 text-sm">Redirecting you to login in a moment...</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-xl p-3 mb-6 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span className="flex-1">{error}</span>
                  <button onClick={() => setError('')} className="text-red-400/60 hover:text-red-400 text-lg leading-none">×</button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">New password</label>
                  <div className="relative">
                    <input
                      type={showPw ? 'text' : 'password'}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 pr-12 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                      placeholder="••••••••"
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {password && (
                    <div className="mt-3 grid grid-cols-2 gap-1.5">
                      {PASSWORD_RULES.map(({ test, label }) => (
                        <div key={label} className={`flex items-center gap-1.5 text-xs ${test(password) ? 'text-green-400' : 'text-gray-500'}`}>
                          <Check className="w-3 h-3" /> {label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Confirm password</label>
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className={`w-full bg-gray-800 border rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 transition-colors ${
                      confirm && password !== confirm
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-700 focus:border-green-500 focus:ring-green-500'
                    }`}
                    placeholder="••••••••"
                  />
                  {confirm && password !== confirm && (
                    <p className="text-red-400 text-xs mt-1">Passwords don't match</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !password || !confirm}
                  className="w-full bg-green-500 hover:bg-green-400 disabled:opacity-50 disabled:cursor-not-allowed text-gray-950 font-bold py-3 rounded-xl transition-colors"
                >
                  {loading ? 'Resetting...' : 'Reset password'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-gray-400 text-sm mt-6">
          <Link to="/login" className="text-green-400 hover:text-green-300">← Back to login</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
